import React, { useState, useCallback } from 'react';
import { Platform, View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import { useAuth } from '../../../providers/AuthProvider';
import { IconButton, MD3Colors, ProgressBar } from 'react-native-paper';
import { Loader } from '../../Common/Loader';
import ConfirmBox from '../../Common/ConfirmBox';
import { useAppConfig } from '../../../providers/AppConfigProvider';

interface AttachmentProps {
  onFileUploaded: (file: string) => void;
  clientAPIURL: string;
  showWarning: (show: boolean) => void;
  warningText: (text: string) => void;
  formMode: string;
  parentId: string;
  listId: string;
  overwrite: boolean;
  getBlobId: (name: string) => string | undefined;
  onAllFilesUploaded?: () => void;
  onUploadStateChange?: (isUploading: boolean) => void;
  uploadEndpoint: string;
}

interface PendingFile {
  id: string;
  file: any;
  name: string;
  size: number;
  type: string;
  uri: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  blobId?: string;
}

const ProjectRecordDocPicker: React.FC<AttachmentProps> = ({ formMode, onFileUploaded, onAllFilesUploaded, onUploadStateChange, clientAPIURL, showWarning, warningText, parentId, listId, overwrite, getBlobId, uploadEndpoint }) => {
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const { getAccessToken } = useAuth();
  const { ALLOWED_EXTENSIONS, MAX_FILE_SIZE_BYTES, MAX_FILES_PER_UPLOAD } = useAppConfig();
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [fileToRemove, setFileToRemove] = useState<string | null>(null);

  const [uploadStats, setUploadStats] = useState({
    total: 0,
    completed: 0,
    failed: 0,
    isUploading: false
  });

  React.useEffect(() => {
    if (onUploadStateChange) {
      onUploadStateChange(uploadStats.isUploading);
    }
  }, [uploadStats.isUploading, onUploadStateChange]);

  // Prevent default browser behavior for drag and drop globally on web
  React.useEffect(() => {
    if (Platform.OS !== 'web') return;

    const preventDefault = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    window.addEventListener('dragover', preventDefault);
    window.addEventListener('drop', preventDefault);

    return () => {
      window.removeEventListener('dragover', preventDefault);
      window.removeEventListener('drop', preventDefault);
    };
  }, []);



  // MAX_FILE_SIZE_BYTES / MAX_FILES_PER_UPLOAD are UX guardrails only — the backend upload
  // endpoints are [DisableRequestSizeLimit] with no server-side enforcement of either.
  const validateFile = (name: string, size: number) => {
    if (!name || name === 'unknown') return true; // Allow if name unknown for now
    
    const docType = name.split('.').pop()?.toLowerCase() || '';
    // If no extension found, but it's an image/doc, it might still be valid
    // But usually we want extensions for security.
    
    if (docType === name.toLowerCase()) {
      // No extension found in the name
      console.log(`File ${name} has no extension`);
    }

    if (!ALLOWED_EXTENSIONS.includes(docType) && docType !== name.toLowerCase()) {
      warningText(`File type .${docType} is not allowed. Allowed: ${ALLOWED_EXTENSIONS.join(', ').toUpperCase()}.`);
      showWarning(true);
      return false;
    }
    if (size > MAX_FILE_SIZE_BYTES) {
      const maxSizeMB = Math.floor(MAX_FILE_SIZE_BYTES / (1024 * 1024));
      warningText(`Selected file is too large to upload. Maximum allowed file size is ${maxSizeMB}MB.`);
      showWarning(true);
      return false;
    }
    return true;
  };

  const processFiles = (assets: any[]) => {
    showWarning(false);

    // Limit files per action
    if (assets.length > MAX_FILES_PER_UPLOAD) {
      warningText(`You can only upload up to ${MAX_FILES_PER_UPLOAD} files at a time. Please select fewer files.`);
      showWarning(true);
      return;
    }

    const newFiles: PendingFile[] = [];

    assets.forEach((asset) => {
      const fileName = asset.name || asset.file?.name || 'unknown';
      const fileSize = asset.size || asset.file?.size || 0;

      if (validateFile(fileName, fileSize)) {
        let finalName = fileName;
        if (finalName.includes(' ')) {
          finalName = finalName.replace(/ /g, '_');
        }

        const blobId = getBlobId(finalName);
        if (blobId && !overwrite) {
          warningText(`File "${finalName}" already exists. Overwrite not enabled.`);
          showWarning(true);
          return;
        }

        newFiles.push({
          id: Math.random().toString(36).substring(7),
          file: asset.file || asset,
          name: finalName,
          size: fileSize,
          type: asset.mimeType || asset.type,
          uri: asset.uri,
          status: 'pending',
          progress: 0,
          blobId: blobId
        });
      }
    });

    if (newFiles.length > 0) {
      setPendingFiles(prev => [...prev, ...newFiles]);
      setUploadStats(prev => ({
        ...prev,
        total: prev.total + newFiles.length,
        isUploading: true
      }));

      // Auto-upload newly added files
      newFiles.forEach(file => {
        uploadSingleFile(file);
      });
    }
  };

  const pickDocument = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'image/*',
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'application/vnd.google-earth.kmz',
          'text/plain'
        ],
        multiple: true
      });

      if (!result.canceled && result.assets) {
        processFiles(result.assets);
      }
    } catch (err) {
      console.error("Error picking document:", err);
    }
  };

  const dropZoneRef = React.useRef<any>(null);

  // Use native DOM listeners for better reliability on Web
  React.useEffect(() => {
    if (Platform.OS !== 'web' || !dropZoneRef.current) return;

    const node = dropZoneRef.current;

    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const onDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const onDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const items = e.dataTransfer?.items;
      if (!items) return;

      const files: any[] = [];
      let folderDetected = false;

      // Use webkitGetAsEntry to accurately detect folders on Web
      const promises = Array.from(items).map(item => {
        const entry = item.webkitGetAsEntry();
        if (entry) {
          if (entry.isFile) {
            return new Promise<void>((resolve) => {
              (entry as any).file((file: File) => {
                files.push({
                  file: file,
                  name: file.name,
                  size: file.size,
                  type: file.type,
                  uri: URL.createObjectURL(file)
                });
                resolve();
              });
            });
          } else if (entry.isDirectory) {
            folderDetected = true;
          }
        }
        return Promise.resolve();
      });

      await Promise.all(promises);

      if (folderDetected) {
        warningText("Folders are not allowed. Only individual files have been processed.");
        showWarning(true);
      }

      if (files.length > 0) {
        processFiles(files);
      }
    };

    node.addEventListener('dragover', onDragOver);
    node.addEventListener('dragenter', onDragEnter);
    node.addEventListener('dragleave', onDragLeave);
    node.addEventListener('drop', onDrop);

    return () => {
      node.removeEventListener('dragover', onDragOver);
      node.removeEventListener('dragenter', onDragEnter);
      node.removeEventListener('dragleave', onDragLeave);
      node.removeEventListener('drop', onDrop);
    };
  }, [dropZoneRef.current]);

  const uploadSingleFile = async (pendingFile: PendingFile) => {
    
    if (pendingFile.status === 'success' || pendingFile.status === 'uploading') return;

    setPendingFiles(prev => prev.map(f => f.id === pendingFile.id ? { ...f, status: 'uploading', progress: 0 } : f));

    try {
      const accessToken = await getAccessToken();
      const formData = new FormData();

      if (Platform.OS === 'web') {
        formData.append("file", pendingFile.file, pendingFile.name);
      } else if (Platform.OS === 'ios') {
        formData.append("file", pendingFile.file);
      } else {
        formData.append("file", {
          uri: pendingFile.uri,
          type: pendingFile.type || "application/octet-stream",
          name: pendingFile.name,
        } as any);
      }

      formData.append("name", pendingFile.name);
      formData.append("type", "file");
      formData.append("parentId", parentId);
      formData.append("listId", listId);
      if (pendingFile.blobId) formData.append("blobId", pendingFile.blobId);
      formData.append("overwrite", overwrite.toString());

      return new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.open("POST", clientAPIURL + uploadEndpoint);
        xhr.setRequestHeader("Authorization", "Bearer " + accessToken);

        // Track progress
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = event.loaded / event.total;
            setPendingFiles(prev => prev.map(f => f.id === pendingFile.id ? { ...f, progress } : f));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            onFileUploaded(xhr.responseText);
            setPendingFiles(prev => prev.map(f => f.id === pendingFile.id ? { ...f, status: 'success', progress: 1 } : f));
            setUploadStats(prev => {
              const newCompleted = prev.completed + 1;
              const isDone = newCompleted + prev.failed === prev.total;
              return {
                ...prev,
                completed: newCompleted,
                isUploading: !isDone
              };
            });
            
            setTimeout(() => {
              setPendingFiles(prev => prev.filter(f => f.id !== pendingFile.id));
              let shouldTriggerComplete = false;
              setUploadStats(prev => {
                if (prev.completed + prev.failed === 0) return prev; // already reset
                if (prev.completed + prev.failed === prev.total && !prev.isUploading && prev.failed === 0) {
                  shouldTriggerComplete = true;
                  return { total: 0, completed: 0, failed: 0, isUploading: false };
                }
                return prev;
              });
              if (shouldTriggerComplete && onAllFilesUploaded) {
                onAllFilesUploaded();
              }
            }, 1500);
            resolve();
          } else {
            handleError();
          }
        };

        xhr.onerror = () => handleError();

        const handleError = () => {
          setPendingFiles(prev => prev.map(f => f.id === pendingFile.id ? { ...f, status: 'error', progress: 0 } : f));
          setUploadStats(prev => {
            const newFailed = prev.failed + 1;
            const isDone = prev.completed + newFailed === prev.total;
            return {
              ...prev,
              failed: newFailed,
              isUploading: !isDone
            };
          });
          warningText(`Failed to upload ${pendingFile.name}. Please try again.`);
          showWarning(true);
          reject(new Error("Upload failed"));
        };

        xhr.send(formData);
      });
    } catch (error) {
      console.error("Upload initialization error:", error);
    }
  };

  const uploadAll = async () => {
    const toUpload = pendingFiles.filter(f => f.status === 'pending' || f.status === 'error');
    setUploadStats(prev => ({ ...prev, isUploading: true, failed: prev.failed - toUpload.length }));
    for (const file of toUpload) {
      await uploadSingleFile(file);
    }
  };

  const removePendingFile = (id: string) => {
    setPendingFiles(prev => prev.filter(f => f.id !== id));
  };

  const confirmRemove = (id: string) => {
    setFileToRemove(id);
    setShowConfirmation(true);
  };

  const handleConfirmRemove = () => {
    if (fileToRemove) {
      removePendingFile(fileToRemove);
    }
    setShowConfirmation(false);
    setFileToRemove(null);
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <Text style={styles.titleText}>Attachments</Text>
      </View>

      {formMode !== "View" && (
        <View ref={dropZoneRef}>
          <TouchableOpacity
            activeOpacity={0.7}
            onPress={pickDocument}
            style={[
              styles.dropZone,
              isDragging && styles.dropZoneActive,
              Platform.OS !== 'web' && styles.mobileDropZone
            ]}
          >
            <IconButton icon="cloud-upload" size={40} iconColor={isDragging ? '#2196F3' : '#666'} />
            <Text style={styles.dropZoneText}>
              {Platform.OS === 'web' ? 'Drag & Drop files here or click to select' : 'Tap to select documents'}
            </Text>
            <View style={styles.infoContainer}>
              <Text style={styles.infoText}>
                Allowed: {ALLOWED_EXTENSIONS.join(', ').toUpperCase()} (Max {Math.floor(MAX_FILE_SIZE_BYTES / (1024 * 1024))}MB)
              </Text>
            </View>
          </TouchableOpacity>
        </View>
      )}

      {uploadStats.total > 0 && (
        <View style={styles.statusContainer}>
          <View style={styles.statusTextRow}>
            <Text style={styles.statusText}>
              {uploadStats.failed > 0 
                ? `${uploadStats.completed} of ${uploadStats.total} uploaded (${uploadStats.failed} failed)`
                : uploadStats.completed === uploadStats.total 
                  ? `All ${uploadStats.total} files uploaded successfully`
                  : `Uploading: ${uploadStats.completed + 1} of ${uploadStats.total}...`
              }
            </Text>
            {uploadStats.failed > 0 && (
              <TouchableOpacity onPress={uploadAll} style={styles.retryLink}>
                <Text style={styles.retryLinkText}>Retry Failed</Text>
              </TouchableOpacity>
            )}
          </View>
          <ProgressBar 
            progress={
              uploadStats.total > 0 
                ? pendingFiles.reduce((acc, f) => acc + f.progress, 0) / uploadStats.total 
                : 0
            } 
            color={uploadStats.failed > 0 ? MD3Colors.error50 : "#2196F3"} 
            style={styles.aggregateProgressBar} 
          />
        </View>
      )}

      {showConfirmation && (
        <ConfirmBox
          title={"Remove File"}
          message={"Are you sure you want to remove this file from the upload list?"}
          yesCallback={handleConfirmRemove}
          noCallback={() => setShowConfirmation(false)}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  headerRow: {
    marginBottom: 8,
  },
  titleText: {
    fontSize: 18,
    fontWeight: '700',
    color: '#212121',
  },
  infoContainer: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#EEEEEE',
    width: '80%',
    alignItems: 'center',
  },
  infoText: {
    fontSize: 11,
    color: '#9E9E9E',
    textAlign: 'center',
    fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : undefined,
  },
  dropZone: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: '#E0E0E0',
    borderRadius: 16,
    padding: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FAFAFA',
    marginTop: 10,
    // transition removed as it's not supported in RN
  },
  dropZoneActive: {
    borderColor: '#2196F3',
    backgroundColor: '#E3F2FD',
    borderStyle: 'solid',
  },
  mobileDropZone: {
    padding: 12,
  },
  dropZoneText: {
    fontSize: 14,
    color: '#616161',
    textAlign: 'center',
    marginTop: 8,
    fontWeight: '500',
  },
  statusContainer: {
    marginTop: 20,
    height: 60, // Fixed height to avoid jumping
    justifyContent: 'center',
  },
  statusTextRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  statusText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#424242',
  },
  aggregateProgressBar: {
    height: 6,
    borderRadius: 3,
    backgroundColor: '#E0E0E0',
  },
  retryLink: {
    padding: 4,
  },
  retryLinkText: {
    fontSize: 13,
    color: '#2196F3',
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
});

export default ProjectRecordDocPicker;
